import { useState, useEffect, RefObject } from 'react';
import { debug } from '@/utils/debug';

/**
 * Hook to track when an element has scrolled out of view,
 * used to show/hide the campaign name in the sticky sidebar.
 *
 * @param elementRef - Ref to the element to observe
 * @param isReady - Optional flag to indicate when the element is available (e.g., after loading)
 */
export function useStickyHeader(
  elementRef: RefObject<HTMLElement | null>,
  isReady: boolean = true
) {
  const [showStickyName, setShowStickyName] = useState(false);

  useEffect(() => {
    // Don't set up observer until ready (element should be in the DOM)
    if (!isReady) {
      debug.log('useStickyHeader: Not ready yet, skipping observer setup');
      return;
    }

    const element = elementRef.current;
    if (!element) {
      debug.log('useStickyHeader: elementRef.current is null, skipping observer setup');
      return;
    }

    debug.log('useStickyHeader: Setting up IntersectionObserver for sticky name');

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show sticky name only when the bottom of the element has scrolled past the top of viewport
        const shouldShow = entry.boundingClientRect.bottom < 0;
        debug.log('useStickyHeader: Intersection changed - shouldShow:', shouldShow, 'bottom position:', entry.boundingClientRect.bottom);
        setShowStickyName(shouldShow);
      },
      {
        threshold: 0,
        rootMargin: '0px', // No margin - detect exact position
      }
    );

    observer.observe(element);

    return () => {
      debug.log('useStickyHeader: Cleaning up IntersectionObserver');
      observer.unobserve(element);
    };
  }, [elementRef, isReady]);

  return showStickyName;
}
